import { AcquirerToVirtualDtoToEntity } from "src/app/features/Acquirer/services/mappers/AcquirerToVirtualDtoToEntity";
import { AcquirerToVirtualEntityToDto } from "src/app/features/Acquirer/services/mappers/AcquirerToVirtualEntityToDto";
import type { AcquirerQueryParams } from "src/app/features/Acquirer/services/server/AcquirerQueryTypes";
import type { AcquirerToVirtualEntity } from "src/app/features/Acquirer/types/subTypes/AcquirerToVirtualEntity";
import { getAssignedDataGroup } from "../../../../../core/router/auth";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { FORM_ACTION } from "../../../../../core/router/types/routerTypes";
import { baseAuthApi } from "../../../../../core/service/baseApi";
import type { FormMutation } from "../../../../../core/service/types/FormMutation";
import type { FormQuery } from "../../../../../core/service/types/FormQuery";
import type { Dto } from "../../../../../core/types/be/Dto";
import type { AcquirerCopyEntity } from "../../types/AcquirerCopyEntity";
import type { AcquirerEntity } from "../../types/AcquirerEntity";
import { AcquirerCopyEntityToCopyDto } from "../mappers/AcquirerCopyEntityToCopyDto";
import { AcquirerPhysicalDtoToEntity } from "../mappers/AcquirerPhysicalDtoToEntity";
import { AcquirerPhysicalEntityToDto } from "../mappers/AcquirerPhysicalEntityToDto";

const AcquirerApi = baseAuthApi.injectEndpoints({
    endpoints: (builder) => ({
        AcquirerLoad: builder.query<AcquirerEntity, FormQuery>({
            providesTags: [ResourceName.ACQUIRER],
            query: (queryParams) => {
                const dataGroup = getAssignedDataGroup();

                return {
                    url: `${ResourceName.DATA_GROUPS}/${dataGroup?.code}/${ResourceName.ACQUIRER_PHYSICAL}/${queryParams.entityId}`,
                    method: "GET",
                };
            },
            transformResponse: (response: Dto, _) => {
                return AcquirerPhysicalDtoToEntity(response);
            },
        }),
        AcquirerSave: builder.mutation<AcquirerEntity, FormMutation<AcquirerEntity>>({
            query: (mutation) => {
                const dataGroup = getAssignedDataGroup();

                const url = mutation.data.physical
                    ? `${ResourceName.DATA_GROUPS}/${dataGroup?.code}/${ResourceName.ACQUIRER_PHYSICAL}?discriminator=PHYSICAL`
                    : `${ResourceName.DATA_GROUPS}/${dataGroup?.code}/${ResourceName.ACQUIRER_VIRTUAL}?discriminator=VIRTUAL`;
                const method = "POST";

                const body = AcquirerPhysicalEntityToDto(mutation.data);

                return {
                    url,
                    method,
                    body,
                };
            },
            invalidatesTags: [ResourceName.ACQUIRER],
        }),
        AcquirerUpdate: builder.mutation<AcquirerEntity, FormMutation<AcquirerEntity>>({
            query: (mutation) => {
                const dataGroup = getAssignedDataGroup();

                const url = mutation.data.physical
                    ? `${ResourceName.DATA_GROUPS}/${dataGroup?.code}/${ResourceName.ACQUIRER_PHYSICAL}/${mutation.data.id}`
                    : `${ResourceName.DATA_GROUPS}/${dataGroup?.code}/${ResourceName.ACQUIRER_VIRTUAL}/${mutation.data.id}`;
                const method = "PUT";

                const body = AcquirerPhysicalEntityToDto(mutation.data);

                return {
                    url,
                    method,
                    body,
                };
            },
            invalidatesTags: [ResourceName.ACQUIRER],
        }),
        AcquirerCopy: builder.mutation<AcquirerEntity, FormMutation<AcquirerCopyEntity>>({
            query: (mutation) => {
                const dataGroup = getAssignedDataGroup();

                const url = `${ResourceName.DATA_GROUPS}/${dataGroup?.code}/${ResourceName.ACQUIRER}/${mutation.data.id}/${FORM_ACTION.COPY}`;
                const method = "POST";

                const body = AcquirerCopyEntityToCopyDto(mutation.data);

                return {
                    url,
                    method,
                    body,
                };
            },
            invalidatesTags: [ResourceName.ACQUIRER],
        }),
        AcquirerLoadPhysicalToVirtual: builder.query<AcquirerToVirtualEntity, FormQuery & { virtualAcquirerId: string }>({
            providesTags: [ResourceName.ACQUIRER],
            query: (queryParams) => {
                const dataGroup = getAssignedDataGroup();
                return {
                    url: `${ResourceName.DATA_GROUPS}/${dataGroup?.code}/${ResourceName.ACQUIRER_VIRTUAL}/${queryParams.virtualAcquirerId}/${ResourceName.ACQUIRERS_TO_VIRTUAL}/${queryParams.entityId}`,
                    method: "GET",
                };
            },
            transformResponse: (response: Dto, _) => {
                return AcquirerToVirtualDtoToEntity(response);
            },
        }),
        AcquirerCreatePhysicalToVirtual: builder.mutation<AcquirerToVirtualEntity, FormMutation<AcquirerToVirtualEntity>>({
            query: (mutation) => {
                const dataGroup = getAssignedDataGroup();
                const body = AcquirerToVirtualEntityToDto(mutation.data);
                return {
                    url: `${ResourceName.DATA_GROUPS}/${dataGroup?.code}/${ResourceName.ACQUIRER_VIRTUAL}/${mutation.data.id}/${ResourceName.ACQUIRERS_TO_VIRTUAL}`,
                    method: "POST",
                    body,
                };
            },
            invalidatesTags: [ResourceName.ACQUIRER],
        }),
        AcquirerUpdatePhysicalToVirtual: builder.mutation<AcquirerToVirtualEntity, FormMutation<AcquirerToVirtualEntity>>({
            query: (mutation) => {
                const dataGroup = getAssignedDataGroup();
                const body = AcquirerToVirtualEntityToDto(mutation.data);
                return {
                    url: `${ResourceName.DATA_GROUPS}/${dataGroup?.code}/${ResourceName.ACQUIRER_VIRTUAL}/${mutation.data.id}/${ResourceName.ACQUIRERS_TO_VIRTUAL}/${mutation.data.id}`,
                    method: "PUT",
                    body,
                };
            },
            invalidatesTags: [ResourceName.ACQUIRER],
        }),
        AcquirerDeletePhysicalToVirtual: builder.mutation<AcquirerEntity, AcquirerQueryParams>({
            query: (acquirerQueryParams) => {
                const dataGroup = getAssignedDataGroup();
                return {
                    url: `${ResourceName.DATA_GROUPS}/${dataGroup?.code}/${ResourceName.ACQUIRER_VIRTUAL}/${acquirerQueryParams.virtualAcquirerId}/${ResourceName.ACQUIRERS_TO_VIRTUAL}/${acquirerQueryParams.acquirerToVirtualId}`,
                    method: "DELETE",
                };
            },
            invalidatesTags: [ResourceName.ACQUIRER],
        }),
    }),
});

export const {
    useAcquirerLoadQuery,
    useAcquirerSaveMutation,
    useAcquirerLoadPhysicalToVirtualQuery,
    useAcquirerUpdateMutation,
    useAcquirerCopyMutation,
    useAcquirerCreatePhysicalToVirtualMutation,
    useAcquirerUpdatePhysicalToVirtualMutation,
    useAcquirerDeletePhysicalToVirtualMutation,
} = AcquirerApi;
